import React, { FC, useState } from 'react';

import { Icon } from '@iconify/react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import Button from '~/components/atoms/buttons/Button';
import StarsAnimatedButton from '~/components/atoms/buttons/StarsAnimatedButton';
import Loader from '~/components/atoms/Loader';
import Benefits from '~/components/molecules/Benefits';
import { Typographies } from '~/theme/typography';

import styles from './styles.module.scss';

const DISCOUNT_PERCENT = 50;
const OLDER_PRICE = `39.99 USD`;
const OLD_PRICE = `27.99 USD`;
const NEW_PRICE = `19.99 USD`;

type Props = {
  onSubmit: () => Promise<void>;
  onDecline: () => void;
};

const Downsell50: FC<Props> = ({ onSubmit, onDecline }) => {
  const { t } = useTranslation('modal');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await onSubmit();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {isLoading && <Loader />}

      <Typography
        variant={Typographies.LABEL_LARGE}
        component="div"
        className={styles.label}
      >
        {t('downsell_v2.percent_off', {
          percent: DISCOUNT_PERCENT,
          defaultValue: '{{percent}}% Off',
        })}
        ! 💰
      </Typography>

      <Typography
        variant={Typographies.TITLE_LARGE}
        component="div"
        className={styles.title}
      >
        {t('downsell_v2.title_v2', {
          percent: DISCOUNT_PERCENT,
          defaultValue: 'Okay… How About {{percent}}% Off?',
        })}{' '}
        🙈
      </Typography>

      <Typography
        variant={Typographies.LABEL_LARGE}
        component="div"
        className={styles.price}
      >
        <div className={styles.price__old}>{OLDER_PRICE}</div>
        <Icon
          icon="material-symbols:arrow-forward-rounded"
          className={styles.price__arrow}
        />
        <div className={styles.price__old}>{OLD_PRICE}</div>
        <Icon
          icon="material-symbols:arrow-forward-rounded"
          className={styles.price__arrow}
        />
        <div className={styles.price__new}>
          {NEW_PRICE}/{t('general:month', 'Month').toLowerCase()}
        </div>
      </Typography>

      <div className={styles.benefits}>
        <Benefits />
      </div>

      <div className={styles.buttons_container}>
        <StarsAnimatedButton
          text={t('downsell_v2.submit_button', {
            percent: DISCOUNT_PERCENT,
            defaultValue: 'Stay & Save {{percent}}%',
          })}
          isDisabled={isLoading}
          className={styles.submit_button}
          onClick={handleSubmit}
        />

        <Button
          color="neutral"
          variant="contained"
          fullWidth
          disabled={isLoading}
          onClick={onDecline}
        >
          {t('general:cancel_anyway', 'Cancel Anyway')}
        </Button>
      </div>
    </div>
  );
};

export default Downsell50;
